import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { vetrineService } from '../../../services/vetrineService';

const FeaturedCarousel = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [featuredStores, setFeaturedStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);

  // Load featured stores from database
  useEffect(() => {
    loadFeaturedStores();
  }, []);

  const loadFeaturedStores = async () => {
    try {
      setLoading(true);
      // Filter at query level: only cliccami or both platform_target, in_evidenza = true
      const featured = await vetrineService?.getFeaturedStoresByPlatform('cliccami');
      
      // Transform to carousel format
      const transformed = featured?.map(store => {
        // Parse gallery images
        let galleryImages = [];
        if (store?.galleryProdotti) {
          try {
            galleryImages = typeof store?.galleryProdotti === 'string' 
              ? JSON.parse(store?.galleryProdotti) 
              : store?.galleryProdotti;
          } catch (e) {
            console.error('Error parsing gallery images:', e);
          }
        }

        // Use first gallery image or main image
        const mainImage = store?.immaginePrincipale || (galleryImages?.[0]) || '/assets/images/no_image.png';

        return {
          id: store?.id,
          name: store?.nomeNegozio,
          category: store?.categoria,
          zone: store?.zona,
          rating: store?.rating || 0,
          reviewCount: store?.numeroRecensioni || 0,
          image: mainImage,
          imageAlt: `${store?.nomeNegozio} - Immagine principale`,
          logo: store?.logoNegozio || '/assets/images/no_image.png',
          logoAlt: `${store?.nomeNegozio} - Logo`,
          badge: store?.tipoVetrina === 'demo' ? 'DEMO' : 'In Evidenza',
          promotion: store?.promoTitolo || null,
          description: store?.descrizioneBreve || `Scopri ${store?.nomeNegozio} nel cuore di ${store?.zona}.`
        };
      });

      setFeaturedStores(transformed);
    } catch (error) {
      console.error('Error loading featured stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalSlides = featuredStores?.length;

  useEffect(() => {
    if (isAutoPlaying && totalSlides > 0) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % totalSlides);
      }, 5000);
    }

    return () => {
      if (autoPlayRef?.current) {
        clearInterval(autoPlayRef?.current);
      }
    };
  }, [isAutoPlaying, totalSlides]);

  const handlePrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  };

  const handleDotClick = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  const handleStoreClick = (storeId) => {
    navigate(`/store-details?id=${storeId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (featuredStores?.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="font-body text-muted-foreground">Nessuna vetrina in evidenza al momento</p>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <div className="overflow-hidden rounded-xl" ref={carouselRef}>
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}>

          {featuredStores?.map((store) =>
          <div key={store?.id} className="w-full flex-shrink-0">
              <div className="relative h-96 md:h-[28rem] lg:h-[32rem] bg-card rounded-xl overflow-hidden shadow-warm-md">
                <div className="absolute inset-0">
                  <Image
                  src={store?.image}
                  alt={store?.imageAlt}
                  className="w-full h-full object-cover" />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                </div>

                <div className="absolute top-4 left-4 right-4 md:top-6 md:left-6 md:right-6 flex items-start justify-between z-10">
                  <div className="flex items-center gap-3 bg-background/95 backdrop-blur-sm rounded-lg px-3 py-2 md:px-4 md:py-2.5">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden flex-shrink-0">
                      <Image
                      src={store?.logo}
                      alt={store?.logoAlt}
                      className="w-full h-full object-cover" />

                    </div>
                    <div className="min-w-0">
                      <p className="font-heading font-semibold text-sm md:text-base text-foreground truncate">
                        {store?.name}
                      </p>
                      <p className="font-caption text-xs text-muted-foreground truncate">
                        {store?.category}
                      </p>
                    </div>
                  </div>

                  <div className="bg-primary text-primary-foreground px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-caption font-semibold text-xs md:text-sm whitespace-nowrap">
                    {store?.badge}
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 lg:p-8 z-10">
                  <div className="max-w-3xl">
                    <div className="flex items-center gap-2 mb-2 md:mb-3">
                      <div className="flex items-center gap-1 bg-background/95 backdrop-blur-sm rounded-lg px-2 py-1 md:px-3 md:py-1.5">
                        <Icon name="MapPin" size={14} className="text-primary flex-shrink-0" />
                        <span className="font-caption text-xs md:text-sm text-foreground whitespace-nowrap">
                          {store?.zone}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 bg-background/95 backdrop-blur-sm rounded-lg px-2 py-1 md:px-3 md:py-1.5">
                        <Icon name="Star" size={14} className="text-warning fill-warning flex-shrink-0" />
                        <span className="font-caption text-xs md:text-sm font-semibold text-foreground whitespace-nowrap">
                          {store?.rating}
                        </span>
                        <span className="font-caption text-xs text-muted-foreground whitespace-nowrap">
                          ({store?.reviewCount})
                        </span>
                      </div>
                    </div>

                    <p className="font-body text-sm md:text-base text-white/90 mb-3 md:mb-4 line-clamp-2">
                      {store?.description}
                    </p>

                    {store?.promotion &&
                  <div className="bg-accent/95 backdrop-blur-sm rounded-lg px-3 py-2 md:px-4 md:py-2.5 mb-4 md:mb-6 inline-flex items-center gap-2">
                        <Icon name="Tag" size={16} className="text-accent-foreground flex-shrink-0" />
                        <span className="font-caption font-semibold text-xs md:text-sm text-accent-foreground">
                          {store?.promotion}
                        </span>
                      </div>
                  }

                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                      variant="default"
                      size="default"
                      iconName="Eye"
                      iconPosition="left"
                      onClick={() => handleStoreClick(store?.id)}
                      className="w-full sm:w-auto">

                        Vedi Vetrina
                      </Button>
                      <Button
                      variant="outline"
                      size="default"
                      iconName="Heart"
                      iconPosition="left"
                      className="w-full sm:w-auto bg-background/95 backdrop-blur-sm hover:bg-background">

                        Salva
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <button
        onClick={handlePrevious}
        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-background/95 backdrop-blur-sm rounded-full flex items-center justify-center transition-smooth hover:bg-background focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 z-20"
        aria-label="Previous slide">

        <Icon name="ChevronLeft" size={20} className="text-foreground" />
      </button>
      <button
        onClick={handleNext}
        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-background/95 backdrop-blur-sm rounded-full flex items-center justify-center transition-smooth hover:bg-background focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 z-20"
        aria-label="Next slide">

        <Icon name="ChevronRight" size={20} className="text-foreground" />
      </button>
      <div className="flex items-center justify-center gap-2 mt-4 md:mt-6">
        {featuredStores?.map((_, index) =>
        <button
          key={index}
          onClick={() => handleDotClick(index)}
          className={`h-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 ${
          index === currentIndex ?
          'w-8 bg-primary' : 'w-2 bg-muted hover:bg-muted-foreground'}`
          }
          aria-label={`Go to slide ${index + 1}`} />

        )}
      </div>
    </div>);

};

export default FeaturedCarousel;